import type { AlbumItem, IAlbumItem } from 'reactApp/modules/albums/albums.types';
import type { AllDocumentsItem } from 'reactApp/modules/allDocuments/allDocuments.types';
import type { AttachesItem } from 'reactApp/modules/attaches/attaches.types';
import type { EditorItem } from 'reactApp/modules/editor/editor.types';
import type { FavoriteFile, FavoriteItem } from 'reactApp/modules/favorites/favorites.types';
import type { FeedItem } from 'reactApp/modules/feed/feed.types';
import type { GalleryItem } from 'reactApp/modules/gallery/gallery.types';
import type { HomeFile, HomeItem } from 'reactApp/modules/home/home.types';
import type { IncomingFolder } from 'reactApp/modules/incoming/incoming.types';
import type { IncomingPublicItem } from 'reactApp/modules/incomingPublic/incomingPublic.types';
import type { DocumentsItem } from 'reactApp/modules/personalDocuments/personalDocuments.types';
import type { PublicFile, PublicItem } from 'reactApp/modules/public/public.types';
import type { SearchFile, SearchItem } from 'reactApp/modules/search/search.types';
import type { SharedFile, SharedItem } from 'reactApp/modules/shared/shared.type';
import type { SharedAutoDeleteFolder } from 'reactApp/modules/sharedAutoDelete/sharedAutoDelete.type';
import type { StockFile, StockItem } from 'reactApp/modules/stock/stock.type';
import type { IStoryFileItem } from 'reactApp/modules/stories/stories.types';
import type { TrashbinItem } from 'reactApp/modules/trashbin/trashbin.types';
import type { QuotaCleanerItem } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.types';
import type { Url } from 'reactApp/types/Tree';

import type { VigboAlbumItem, VigboItem } from '../vigbo/vigbo.types';

export type CloudItem = {
    url?: Url;
    weblink?: string;
} & (
    | HomeItem
    | PublicItem
    | SharedItem
    | FavoriteItem
    | StockItem
    | SearchItem
    | IncomingFolder
    | AttachesItem
    | TrashbinItem
    | FeedItem
    | EditorItem
    | GalleryItem
    | SharedAutoDeleteFolder
    | DocumentsItem
    | IStoryFileItem
    | AlbumItem
    | QuotaCleanerItem
    | AllDocumentsItem
    | IncomingPublicItem
    | VigboItem
);

export type CloudFile =
    | HomeFile
    | PublicFile
    | SharedFile
    | FavoriteFile
    | StockFile
    | SearchFile
    | AttachesItem
    | FeedItem
    | EditorItem
    | GalleryItem
    | DocumentsItem
    | IAlbumItem
    | AllDocumentsItem
    | VigboAlbumItem;

export enum EStorageType {
    home = 'home',
    'public' = 'public',
    favorites = 'favorites',
    sharedIncoming = 'shared/incoming',
    sharedLinks = 'shared/links',
    sharedAutodelete = 'shared/autodelete',
    search = 'search',
    attaches = 'attaches',
    viewerAttaches = 'viewer/attaches',
    stock = 'stock',
    trashbin = 'trashbin',
    editor = 'editor',
    r7 = 'r-seven/edit',
    recommend = 'recommend',
    feed = 'feed',
    start = 'start',
    gallery = 'gallery',
    documents = 'documents',
    archive = 'archive',
    myoffice = 'myoffice/edit/home',
    myofficeAttaches = 'myoffice/edit/attaches',
    story = 'story',
    albums = 'albums',
    subscriptions = 'subscriptions',
    quotaCleaner = 'promo/quota',
    quotaTariffs = 'promo/quota/tariffs',
    quotaPartner = 'promo/quota/partner',
    promocodes = 'promocodes',
    embedded = 'embedded',
    alldocuments = 'alldocuments',
    pdfEdit = 'pdf/edit',
    integration = 'integration',
    family = 'family',
    familyFolders = 'family/folders',

    familyAlbums = 'family/albums',

    familyLanding = 'promo/family',
    r7wopi = 'r7w/edit/home',
    safe = 'safe',
    inlineIntegration = 'inline-integration',
    attachesViewerDocuments = 'attaches/documents',
    attachesViewerError = 'attaches/error',
    templates = 'templates',
    sharingAttach = 'sharing-attach',
    incomingPublic = 'incoming-public',

    vigbo = 'vigbo',
}

export const INTEGRATION_STORAGES = {
    [EStorageType.integration]: EStorageType.integration,
    [EStorageType.inlineIntegration]: EStorageType.inlineIntegration,
    [EStorageType.sharingAttach]: EStorageType.sharingAttach,
};

export enum EActions {
    copy = 'copy',
    move = 'move',
    remove = 'remove',
    removeFromAlbums = 'removeFromAlbums',
    removeIncomingPublic = 'removeIncomingPublic',
    rename = 'rename',
    history = 'history',
    addToFavorites = 'addToFavorites',
    removeFromFavorites = 'removeFromFavorites',
    publish = 'publish',
    unpublish = 'unpublish',
    share = 'share',
    shareLink = 'shareLink',
    openParentFolder = 'openParentFolder',
    download = 'download',
    add = 'add',
    upload = 'upload',
    uploadFolder = 'uploadFolder',
    uploadFile = 'uploadFile',
    clone = 'clone',
    view = 'view',
    sort = 'sort',
    restoreAll = 'restoreAll',
    restore = 'restore',
    clear = 'clear',
    sendViaMail = 'sendViaMail',
    mount = 'mount',
    selectAll = 'selectAll',
    authorSort = 'authorSort',
    favorites = 'favorites',
    create = 'create',
    forward = 'forward',
    more = 'more',
    viewThumbs = 'viewThumbs',
    viewList = 'viewList',
    viewGallery = 'viewGallery',
    sortName = 'sortName',
    sortSize = 'sortSize',
    sortDate = 'sortDate',
    createFolder = 'createFolder',
    createFamilyFolder = 'createFamilyFolder',
    createDoc = 'createDoc',
    createCell = 'createCell',
    createPpt = 'createPpt',
    back = 'back',
    facesFilter = 'facesFilter',
    publishAlbum = 'publishAlbum',
    addToCustomAlbum = 'addToCustomAlbum',
    addToVigboAlbum = 'addToVigboAlbum',
    yearFilter = 'yearFilter',
    newFeatures = 'newFeatures',
    showSelected = 'showSelected',
    fullscreen = 'fullscreen',
    options = 'options',
    bookmark = 'bookmark',
    delimiter = 'delimiter',
    createAlbum = 'createAlbum',
    // tempexp_17340-start
    changeDesign = 'changeDesign',
    changeDesignMore = 'changeDesignMore',
    // tempexp_17340-end
    complain = 'complain',
    // tempexp_18662-next-line
    publicInfo = 'publicInfo',
    restoreSafe = 'restoreSafe',

    createVigboGallery = 'createVigboGallery',
    goToVigbo = 'goToVigbo',
    watchVigboGallery = 'watchVigboGallery',
    setupVigboGallery = 'setupVigboGallery',
    addToGallery = 'addToGallery',
    makeFamilyFolder = 'makeFamilyFolder',
    makeFamilyFolderCommon = 'makeFamilyFolderCommon',
}

export interface IEnabledActions {
    [EActions.copy]: boolean;
    [EActions.move]: boolean;
    [EActions.remove]: boolean;
    [EActions.rename]: boolean;
    [EActions.history]: boolean;
    [EActions.favorites]: boolean;
    [EActions.publish]: boolean;
    [EActions.share]: boolean;
    [EActions.openParentFolder]: boolean;
    [EActions.download]: boolean;
    [EActions.add]: boolean;
    [EActions.view]: boolean;
    [EActions.sort]: boolean;
    [EActions.selectAll]: boolean;
    [EActions.mount]: boolean;
    [EActions.sendViaMail]: boolean;
    [EActions.facesFilter]: boolean;
    [EActions.publishAlbum]: boolean;
    [EActions.createAlbum]: boolean;
    // tempexp_17340-start
    [EActions.changeDesign]: boolean;
    [EActions.changeDesignMore]: boolean;
    // tempexp_17340-end
    [EActions.complain]: boolean;
    // tempexp_18662-next-line
    [EActions.publicInfo]: boolean;

    [EActions.createVigboGallery]: boolean;
    [EActions.goToVigbo]: boolean;
    [EActions.watchVigboGallery]: boolean;
    [EActions.setupVigboGallery]: boolean;
    [EActions.addToGallery]: boolean;
    [EActions.makeFamilyFolder]: boolean;
    [EActions.makeFamilyFolderCommon]: boolean;
}

export interface IStorageState {
    selectedItem: string;
}

export interface ItemMountedShared {
    isDomain?: boolean;
    isMounted?: boolean;
    isShared?: boolean;
    isReadOnly?: boolean;
}
